import { useCallback, useState } from 'react';
import { usePostFileMutation } from '../../../../redux/api/fymAPI';
import { getFullAPIFileURL } from '../../../common/getFullAPIFileURL';

/** The largest picture that the upload takes (design cover-editor c3: "up to 25 MB"). */
export const COVER_UPLOAD_MAX_BYTES = 25 * 1024 * 1024;
export const COVER_UPLOAD_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export type CoverUploadError = 'type' | 'size' | 'failed';

/** Why a picked file cannot be used, or null. Pure. */
export function coverUploadRefusal(file: { type: string; size: number }): CoverUploadError | null {
  if (!COVER_UPLOAD_TYPES.includes(file.type)) {
    return 'type';
  }
  return file.size > COVER_UPLOAD_MAX_BYTES ? 'size' : null;
}

/**
 * Uploads one picture of the artist (`POST /accounts/:id/files`, the upload of every cover): a
 * reference of an AI request, or an own background. JPEG, PNG or WebP, at most 25 MB.
 */
export function useCoverPictureUpload(accountId: number) {
  const [postFile, upload] = usePostFileMutation();
  const [error, setError] = useState<CoverUploadError | null>(null);

  const uploadPicture = useCallback(
    async (file: File): Promise<{ fileId: number; url: string } | null> => {
      const refusal = coverUploadRefusal(file);
      if (refusal !== null) {
        setError(refusal);
        return null;
      }
      setError(null);
      const formData = new FormData();
      formData.append('file', file, file.name);
      try {
        const saved = await postFile({ accountId, formData }).unwrap();
        return { fileId: saved.id, url: getFullAPIFileURL(saved.url) };
      } catch (caught) {
        console.error('Cover editor: the picture did not upload', caught);
        setError('failed');
        return null;
      }
    },
    [accountId, postFile]
  );

  return { uploadPicture, isUploading: upload.isLoading, error };
}
